import { findTabbableAfter } from './tabbableHelper';

export interface FieldFocusRequest {
  field: string;
  moveToNextField: boolean;
}

/** The position of the list card that opened a detail screen. */
export interface ItemFocusRequest {
  itemIndex: number;
}

export type ReturnFocusRequest = FieldFocusRequest | ItemFocusRequest;

const LIST_ITEM_SELECTOR = '[data-keyboard-item]';

/** A field's own Change and clear buttons sit in its form item, so the next field starts after it. */
export const findNextField = (field: HTMLElement) =>
  findTabbableAfter(
    field.closest<HTMLElement>('.ant-form-item') ?? field,
    field.closest('form') ?? field.ownerDocument.body,
  );

/** Refocuses the `data-field` that opened a picker, or the field after it once a value was picked. */
export const focusReturnedField = (
  layout: HTMLElement,
  { field, moveToNextField }: FieldFocusRequest,
) =>
  Array.from(layout.querySelectorAll<HTMLElement>(`[data-field="${field}"]`)).some(
    (openingField) => {
      const target = (moveToNextField && findNextField(openingField)) || openingField;
      target.focus();
      return target.ownerDocument.activeElement === target;
    },
  );

/** The list remounts on the way back, so the card is found again by where it sat in the list. */
export const getFocusedItemIndex = (doc: Document = document) => {
  const item = doc.activeElement?.closest(LIST_ITEM_SELECTOR);
  if (!item) return undefined;
  return Array.from(doc.querySelectorAll(LIST_ITEM_SELECTOR)).indexOf(item);
};

const focusReturnedItem = (layout: HTMLElement, { itemIndex }: ItemFocusRequest) => {
  const item = layout.querySelectorAll<HTMLElement>(LIST_ITEM_SELECTOR)[itemIndex];
  if (!item) return false;
  item.focus();
  return item.ownerDocument.activeElement === item;
};

export const focusReturnTarget = (layout: HTMLElement, request: ReturnFocusRequest) =>
  'itemIndex' in request ? focusReturnedItem(layout, request) : focusReturnedField(layout, request);
